import { Button } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Alert } from 'components/Alert';

import { locales } from './constants';

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      width: '100%',
      borderRadius: 0,
      border: '3px solid transparent',
      flexWrap: 'wrap',
    },
    buttonGroup: {
      display: 'flex',
      gap: theme.spacing(1),
    },
  }),
);

export interface ApplicationFormLanguageBannerProps {
  displayLocale: string;
  funnelLocale: string;
  availableLocales: Record<string, unknown>;
  onTranslate: (locale: string) => void;
}

export const ApplicationFormLanguageBanner: React.VFC<
  ApplicationFormLanguageBannerProps
> = ({ displayLocale, funnelLocale, availableLocales, onTranslate }) => {
  const classes = useStyles();
  const intl = useIntl();

  const [showBanner, setShowBanner] = useState(false);

  const handleClose = () => {
    setShowBanner(false);
    localStorage.setItem('__applicant_locale_banner', 'closed');
  };

  const handleLanguageChange = (locale: string) => {
    onTranslate(locale);
    setShowBanner(false);
  };

  const browserLanguage = Object.keys(availableLocales).find(locale =>
    locale.includes(navigator.language),
  );
  const browserLanguageAsObject = locales.find(
    locale => locale.code === browserLanguage,
  );

  useEffect(() => {
    if (
      Object.keys(availableLocales).length &&
      browserLanguage &&
      browserLanguage !== funnelLocale &&
      browserLanguage !== displayLocale &&
      browserLanguageAsObject
    ) {
      setShowBanner(true);
    }
  }, [
    browserLanguage,
    browserLanguageAsObject,
    funnelLocale,
    displayLocale,
    availableLocales,
  ]);

  if (localStorage.getItem('__applicant_locale_banner') === 'closed') {
    return null;
  }

  if (!showBanner) {
    return null;
  }

  return (
    <Alert
      severity="info"
      icon={false}
      className={classes.root}
      action={
        <div className={classes.buttonGroup}>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {intl.formatMessage({
              defaultMessage: 'Dismiss',
              description: 'Button to dismiss the language banner',
            })}
          </Button>

          {browserLanguageAsObject && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleLanguageChange(browserLanguageAsObject.code)}
            >
              <FormattedMessage
                defaultMessage="View in"
                description="Text before the language code in the language banner"
              />{' '}
              {browserLanguageAsObject.shortCode} {browserLanguageAsObject.flag}
            </Button>
          )}
        </div>
      }
    >
      <div>
        {intl.formatMessage({
          defaultMessage:
            'We detected your browser language is different than the application language. Would you like to apply to this job in your language?',
          description:
            'Descriptive text in the language banner. Indicate that the applicant language is in a different language than the funnel',
        })}
      </div>
    </Alert>
  );
};
